import { Button } from "@/modern/components/ui/button";
import { FileText, Target, Users, Award, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useContext, useState, useEffect } from "react";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { BaseModal } from "../../base/BaseModal.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";
import { SWIVU_ASSIST_CONFIG } from "@/other/constants/OtherKeys.jsx";
import { SpotIncentiveFormData } from "./spotIncentiveSchema";
import { getSpotIncentiveDetails } from "@/network/network.jsx";
import { toast } from "sonner";
import SpotIncentiveImagePreview, { ImageThumbnail } from "./SpotIncentiveImagePreview";

interface SpotIncentivePreviewProps {
  data: SpotIncentiveFormData & { id?: number };
  editorInstance?: unknown;
  onClose?: () => void;
}

const SpotIncentivePreview = ({
  data,
  editorInstance,
  onClose
}: SpotIncentivePreviewProps) => {
  const { project } = useContext(BryntumContext) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState<any[]>([]); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [error, setError] = useState<string | null>(null);

  // Auto-open when component mounts
  useEffect(() => {
    setIsOpen(true);
  }, []);

  // Fetch details when component mounts and we have an ID
  useEffect(() => {
    const fetchDetails = async () => {
      if (!data.id) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await getSpotIncentiveDetails(data.id);
        setDetailsData(response.data || []);
      } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
        const errorMessage = err?.response?.data?.detail || 'Failed to load spot incentive details';
        setError(errorMessage);
        toast.error("Failed to load details", {
          description: errorMessage
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [data.id]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  // Helper functions to get real data from Bryntum stores
  const getSelectedTask = (taskId: number | null) => {
    if (!taskId) return undefined;
    return project?.extraTaskStore?.getById(taskId);
  };

  const getSelectedResources = (resourceIds: number[]) => {
    return resourceIds.map(id => project?.resourceStore?.getById(id)).filter(Boolean);
  };

  const getTagName = (tagId: number) => {
    const tag = project?.incentiveTagsStore?.getById(tagId);
    return tag?.name || `Tag ${tagId}`;
  };

  const getTotalPoints = () => {
    const positivePoints = data.positiveTagIds.reduce((sum: number, tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      return sum + (tag?.categoryRecord?.points || 0);
    }, 0);

    const negativePoints = data.negativeTagIds.reduce((sum: number, tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      return sum + (tag?.categoryRecord?.points || 0);
    }, 0);

    return positivePoints - negativePoints;
  };


  const hasPointsAssigned = data.positiveTagIds.length > 0 || data.negativeTagIds.length > 0;
  const totalPoints = getTotalPoints();

  const MODAL_SIZE_CONFIG = {
    width: "min(90vw, 48rem)",
    height: "min(90vh, 47rem)",
    minWidth: "min(90vw, 48rem)",
    minHeight: "min(90vh, 47rem)",
    maxWidth: "min(90vw, 55rem)",
    maxHeight: "min(90vh, 50rem)"
  };

  if (!isOpen) return null;

  return (
    <BaseModal
        title={data.name}
        description=""
        onClose={handleClose}
        size={MODAL_SIZE_CONFIG}
        editorInstance={editorInstance}
        record={data}
        headerIconCls=""
        modal={false}
        version=""
        className="!resize-none [&_.border-t]:!border-t-0"
        swivuAssistanceConfig={SWIVU_ASSIST_CONFIG.spotIncentive}
        footerRenderer={() => (
          <div className="flex justify-end gap-3 pt-3 flex-shrink-0 !border-t-0">
            <Button
              variant="default"
              onClick={handleClose}
            >
              Done
            </Button>
          </div>
        )}
      >
      <BaseModalBody>
        <div className="space-y-4 overflow-y-auto flex-1 pt-1 pb-4">
          {/* Task Section - What work */}
          {data.taskId && (
            <div className="bg-white dark:bg-card rounded-md border p-3">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-bold">Task</h3>
              </div>
              <p className="text-sm font-medium">{getSelectedTask(data.taskId)?.name}</p>
            </div>
          )}

          {/* Resources Section - Who did it */}
          {data.resourceIds.length > 0 && (
            <div className="bg-white dark:bg-card rounded-md border p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-bold">Resources ({data.resourceIds.length})</h3>
                </div>
                <span className="text-sm text-muted-foreground">
                  {data.startDate && data.endDate && (
                    <>
                      {format(new Date(data.startDate), "MMM dd")} - {format(new Date(data.endDate), "MMM dd, yyyy")}
                    </>
                  )}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {getSelectedResources(data.resourceIds).map((resource) => (
                  <div key={resource.id} className="rounded-md p-2.5 bg-[#f4f4f4] dark:bg-[#292524c7]">
                    <p className="text-sm font-medium">{resource.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Details Section - Evidence/Why */}
          {isLoading ? (
            <div className="bg-white dark:bg-card rounded-md border p-4">
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading details...</span>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="bg-white dark:bg-card rounded-md border p-4">
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="text-sm text-destructive mb-2">{error}</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Retry fetching details
                      if (data.id) {
                        setIsLoading(true);
                        setError(null);
                        getSpotIncentiveDetails(data.id)
                          .then(response => {
                            setDetailsData(response.data || []);
                            setIsLoading(false);
                          })
                          .catch(err => {
                            const errorMessage = err?.response?.data?.detail || 'Failed to load spot incentive details';
                            setError(errorMessage);
                            setIsLoading(false);
                            toast.error("Failed to load details", {
                              description: errorMessage
                            });
                          });
                      }
                    }}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          ) : detailsData.length > 0 && (
            <SpotIncentiveImagePreview
              images={detailsData
                .filter(detail => detail.image_url)
                .map(detail => ({
                  src: detail.image_url,
                  alt: detail.comment || 'Detail image',
                  caption: detail.comment
                }))}
              allowZoom={true}
            >
              <div className="bg-white dark:bg-card rounded-md border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-bold">Details ({detailsData.length})</h3>
                </div>
                <div className="space-y-3">
                  {detailsData.map((detail, index) => {
                    const imageIndex = detailsData
                      .slice(0, index + 1)
                      .filter(d => d.image_url).length - 1;

                    return (
                      <div key={detail.id || index} className="flex gap-3 pb-3 border-b last:border-0 last:pb-0">
                        {detail.image_url && (
                          <ImageThumbnail
                            src={detail.image_thumbnail_url || detail.image_url}
                            alt="Detail"
                            index={imageIndex}
                            className="w-20 h-20 rounded-md overflow-hidden bg-gray-100 dark:bg-muted flex-shrink-0 border cursor-pointer hover:opacity-80 transition-opacity"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm leading-relaxed">{detail.comment}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </SpotIncentiveImagePreview>
          )}

          {/* Points Outcome - The Reward */}
          {hasPointsAssigned && (
            <div className="bg-white dark:bg-card rounded-md border p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-bold">Points Awarded</h3>
                </div>
                <div className={`flex items-center justify-center rounded-md px-3 py-2 font-bold text-base ${
                  totalPoints >= 0 ? 'bg-gray-50 dark:bg-muted/30 text-success' : 'bg-gray-50 dark:bg-muted/30 text-destructive'
                }`}>
                  {totalPoints >= 0 ? "+" : ""}{totalPoints}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Positive Tags */}
                {data.positiveTagIds.map((tagId) => {
                  const tag = project?.incentiveTagsStore?.getById(tagId);
                  const points = tag?.categoryRecord?.points || 0;
                  return (
                    <div key={`positive-${tagId}`}
                    className="flex items-start justify-between rounded-md p-2.5 bg-[#f4f4f4] dark:bg-[#292524c7] gap-2">
                      <span className="text-sm font-medium flex-1 min-w-0 break-words">{getTagName(tagId)}</span>
                      <span className="text-sm font-medium text-success flex-shrink-0">+{points} pts</span>
                    </div>
                  );
                })}
                {/* Negative Tags */}
                {data.negativeTagIds.map((tagId) => {
                  const tag = project?.incentiveTagsStore?.getById(tagId);
                  const points = tag?.categoryRecord?.points || 0;
                  return (
                    <div key={`negative-${tagId}`}
                         className="flex items-start justify-between bg-[#f4f4f4] dark:bg-[#292524c7] rounded-md px-3 py-2 gap-2">
                      <span className="text-sm font-medium flex-1 min-w-0 break-words">{getTagName(tagId)}</span>
                      <span className="text-sm font-medium text-destructive flex-shrink-0">-{points} pts</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </BaseModalBody>
      </BaseModal>
  );
};

export default SpotIncentivePreview;
